import { Marked } from "marked";
import DOMPurify from "dompurify";

// Own instance: src/app/shared/Marked.jsx installs a global list renderer
// that flattens list items to raw text, which would break the numbered
// steps (and the bold UI labels inside them) these articles rely on.
const guideMarked = new Marked({ gfm: true });

/** Article markdown → HTML. Articles are written in this repo, never by users. */
export const renderArticle = (markdown) => guideMarked.parse(markdown ?? "");

// Ask AI answers will come from a model, so unlike the articles they are not
// trusted: raw HTML is shown as text, and DOMPurify keeps only the markup an
// answer needs. Links stay as written so #article and /route links work.
const answerMarked = new Marked({
   gfm: true,
   breaks: true,
   renderer: {
      html: (token) =>
         String(token?.raw ?? token?.text ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
   }
});

const ANSWER_ALLOWED = {
   ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "del",
      "code",
      "pre",
      "blockquote",
      "ul",
      "ol",
      "li",
      "h2",
      "h3",
      "h4",
      "a",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td"
   ],
   ALLOWED_ATTR: ["href"]
};

/** Ask AI answer markdown → HTML that is safe to inject. */
export const renderAnswer = (markdown) =>
   DOMPurify.sanitize(answerMarked.parse(markdown ?? ""), ANSWER_ALLOWED);

/**
 * Ask AI answers from the knowledge base, whose chapters outnumber the User
 * Guide's articles. A [text](#id) link to a chapter with no article here would
 * do nothing when clicked, so it is shown as plain text instead.
 */
export const unlinkMissingArticles = (markdown, articles) =>
   (markdown ?? "").replace(/\[([^\]]*)\]\(#([^)\s]+)\)/g, (link, text, id) =>
      articles.some((article) => article.id === id) ? link : text
   );

/** Article markdown → plain words, so search ignores the markup. */
export const toPlainText = (markdown = "") =>
   markdown
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/[#>*_`|]/g, " ")
      .replace(/^\s*(\d+\.|-)\s+/gm, " ")
      .replace(/-{3,}/g, " ")
      .replace(/\s+/g, " ")
      .trim();

/**
 * The items this user can act on: an item without `roles` is for everyone,
 * otherwise the user needs one of the listed roles.
 */
export const filterByRoles = (items, isAuthorized) =>
   items.filter((item) => !item.roles || isAuthorized(item.roles));

/** The articles this user can see, with their body as searchable plain text. */
export const getVisibleArticles = (articles, isAuthorized) =>
   filterByRoles(articles, isAuthorized).map((article) => ({
      ...article,
      plainText: toPlainText(article.body)
   }));

/** Fields the User Guide search looks in. */
export const articleSearchFields = (article) => [
   article.title,
   article.summary,
   article.category,
   article.plainText
];
